import { type FilterOptions } from '../types/filters';

/**
 * Filter state interface for internal use
 * Uses termID-based filtering for better performance
 */
export interface FilterState {
  // Price filtering
  minPrice?: number;
  maxPrice?: number;

  // Category filtering using termIDs
  selectedCategories: number[];
  selectedSubcategory?: number;
  selectedSubSubcategory?: number;

  // Taxonomy filtering using termIDs
  selectedBrands: number[];
  selectedSkinTypes: number[];
  selectedSkinConcerns: number[];
  selectedIngredients: number[];
}

/**
 * Build filter options from current filter state
 * Handles the priority logic for different filter types using termIDs
 */
export const buildFiltersFromState = (state: FilterState): FilterOptions => {
  const {
    minPrice,
    maxPrice,
    selectedCategories,
    selectedSubcategory,
    selectedSubSubcategory,
    selectedBrands,
    selectedSkinTypes,
    selectedSkinConcerns,
    selectedIngredients,
  } = state;

  // Base filter object
  const baseFilters: FilterOptions = {};

  // Add price filters if set
  if (minPrice !== undefined) {
    baseFilters.minPrice = minPrice;
  }
  if (maxPrice !== undefined) {
    baseFilters.maxPrice = maxPrice;
  }

  // Add taxonomy filters if they have values
  if (selectedBrands.length > 0) {
    baseFilters.brands = selectedBrands;
  }
  if (selectedSkinTypes.length > 0) {
    baseFilters.skinTypes = selectedSkinTypes;
  }
  if (selectedSkinConcerns.length > 0) {
    baseFilters.skinConcerns = selectedSkinConcerns;
  }
  if (selectedIngredients.length > 0) {
    baseFilters.ingredients = selectedIngredients;
  }

  // Priority logic for categories using termIDs
  if (selectedSubSubcategory) {
    // Sub-subcategory has highest priority
    baseFilters.categories = [selectedSubSubcategory];
  } else if (selectedSubcategory) {
    // Subcategory has second priority
    baseFilters.categories = [selectedSubcategory];
  } else if (selectedCategories.length > 0) {
    // Main category has lowest priority
    baseFilters.categories = selectedCategories;
  }

  return baseFilters;
};

/**
 * Check if the current filter state has meaningful selections
 * Used to determine if filter updates should be triggered
 */
export const hasMeaningfulFilters = (state: FilterState): boolean => {
  const {
    minPrice,
    maxPrice,
    selectedCategories,
    selectedSubcategory,
    selectedSubSubcategory,
    selectedBrands,
    selectedSkinTypes,
    selectedSkinConcerns,
    selectedIngredients,
  } = state;

  return (
    (minPrice !== undefined && minPrice > 0) ||
    (maxPrice !== undefined && maxPrice < 1000) ||
    selectedCategories.length > 0 ||
    Boolean(selectedSubcategory) ||
    Boolean(selectedSubSubcategory) ||
    selectedBrands.length > 0 ||
    selectedSkinTypes.length > 0 ||
    selectedSkinConcerns.length > 0 ||
    selectedIngredients.length > 0
  );
};

/**
 * Clear all filter selections - returns initial state
 */
export const getClearedFilterState = (): Partial<FilterState> => ({
  minPrice: undefined,
  maxPrice: undefined,
  selectedCategories: [],
  selectedSubcategory: undefined,
  selectedSubSubcategory: undefined,
  selectedBrands: [],
  selectedSkinTypes: [],
  selectedSkinConcerns: [],
  selectedIngredients: [],
});
